import { z } from 'zod';
import type { PlanFeatures } from './limits.js';

/**
 * Database provisioning — the panel's surface over Coolify's eight
 * standalone-database engines (POST /databases/<engine>).
 *
 * Credential handling is the defining constraint here (ADR-0007): the panel
 * generates credentials itself, forwards them to Coolify at creation, and
 * returns them exactly once in the create response. They are never stored
 * in the panel database, never audit-logged, and the live-state mapper
 * never exposes Coolify's credential fields — so "shown once" holds even
 * though Coolify would happily return some of them on reads.
 */

export const DATABASE_ENGINES = [
  'postgresql',
  'mysql',
  'mariadb',
  'mongodb',
  'redis',
  'clickhouse',
  'dragonfly',
  'keydb',
] as const;
export type DatabaseEngine = (typeof DATABASE_ENGINES)[number];

/**
 * UI hints per engine. `imagePlaceholder` mirrors Coolify's default image so
 * staff see what they get when they don't pin one; pinning any other
 * tag/image is the version-pinning path.
 */
export const DATABASE_ENGINE_INFO: Record<
  DatabaseEngine,
  { label: string; imagePlaceholder: string; internalPort: number }
> = {
  postgresql: { label: 'PostgreSQL', imagePlaceholder: 'postgres:16-alpine', internalPort: 5432 },
  mysql: { label: 'MySQL', imagePlaceholder: 'mysql:8', internalPort: 3306 },
  mariadb: { label: 'MariaDB', imagePlaceholder: 'mariadb:11', internalPort: 3306 },
  mongodb: { label: 'MongoDB', imagePlaceholder: 'mongo:7', internalPort: 27017 },
  redis: { label: 'Redis', imagePlaceholder: 'redis:7-alpine', internalPort: 6379 },
  clickhouse: {
    label: 'ClickHouse',
    imagePlaceholder: 'clickhouse/clickhouse-server:latest',
    internalPort: 9000,
  },
  dragonfly: {
    label: 'Dragonfly',
    imagePlaceholder: 'docker.dragonflydb.io/dragonflydb/dragonfly:latest',
    internalPort: 6379,
  },
  keydb: { label: 'KeyDB', imagePlaceholder: 'eqalpha/keydb:latest', internalPort: 6379 },
};

// ── Create / update ─────────────────────────────────────────────────

const publicAccessRule = (data: { isPublic?: boolean; publicPort?: number }) =>
  !data.isPublic || data.publicPort !== undefined;

export const createDatabaseSchema = z
  .object({
    subscriptionId: z.string().uuid(),
    /** Which Coolify server runs the database container. */
    serverUuid: z.string().trim().min(1).max(100),
    engine: z.enum(DATABASE_ENGINES),
    name: z.string().trim().min(1).max(200),
    description: z.string().max(2000).nullish(),
    /** Pin a Docker image/tag (e.g. "postgres:16-alpine"); Coolify's engine default otherwise. */
    image: z.string().trim().min(1).max(200).optional(),
    /**
     * Expose the database on a host port (Coolify's is_public + public_port).
     * Gated on the plan's `database_external_access` feature.
     */
    isPublic: z.boolean().default(false),
    /** Coolify accepts 1024–65535 only. */
    publicPort: z.number().int().min(1024).max(65535).optional(),
    /** Start the container right after creation. */
    instantDeploy: z.boolean().default(false),
  })
  .refine(publicAccessRule, {
    message: 'publicPort is required when isPublic is enabled',
    path: ['publicPort'],
  });
export type CreateDatabaseInput = z.infer<typeof createDatabaseSchema>;

export const updateDatabaseSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    description: z.string().max(2000).nullish(),
    image: z.string().trim().min(1).max(200).optional(),
    isPublic: z.boolean().optional(),
    publicPort: z.number().int().min(1024).max(65535).optional(),
  })
  .refine((data) => data.isPublic !== true || data.publicPort !== undefined, {
    message: 'publicPort is required when enabling isPublic',
    path: ['publicPort'],
  });
export type UpdateDatabaseInput = z.infer<typeof updateDatabaseSchema>;

/** Repair endpoint input, same as applications (ADR-0006 reservation pattern). */
export const linkDatabaseSchema = z.object({
  coolifyUuid: z.string().trim().min(1).max(100),
});
export type LinkDatabaseInput = z.infer<typeof linkDatabaseSchema>;

// ── DTOs ────────────────────────────────────────────────────────────

/**
 * The reveal-once credential set returned by POST /api/databases — the only
 * place these values ever appear. Engines without a concept (redis has no
 * username or database) carry null.
 */
export interface DatabaseCredentialsDto {
  username: string | null;
  password: string;
  /** mysql/mariadb also get a root password. */
  rootPassword: string | null;
  database: string | null;
  /** Container-network connection string as reported by Coolify, if any. */
  internalUrl: string | null;
}

/**
 * Live database state read from Coolify, credential fields deliberately
 * excluded (reveal-once: values shown at creation are never re-displayed).
 */
export interface CoolifyDatabaseDto {
  uuid: string;
  name: string;
  description: string | null;
  status: string | null;
  /** Coolify's resource type, e.g. "standalone-postgresql". */
  type: string | null;
  image: string | null;
  isPublic: boolean;
  publicPort: number | null;
  limitsMemory: string | null;
  limitsCpus: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface DatabaseDetailDto {
  resource: {
    id: string;
    subscriptionId: string;
    name: string;
    coolifyUuid: string | null;
    status: string;
    details: Record<string, unknown> | null;
    createdAt: string;
  };
  subscription: {
    id: string;
    name: string;
    clientId: string;
    clientName: string;
    status: string;
  };
  features: PlanFeatures;
  live: CoolifyDatabaseDto | null;
  liveError: string | null;
  /**
   * Coolify instance URL for "open in Coolify" links — runtime logs for
   * databases are not available through the Coolify API, only its UI.
   */
  coolifyBaseUrl: string | null;
}
